import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  QualificationId,
  QualificationTrainingProviderClass,
  QualificationV2,
} from '@wilson/interfaces';
import {
  QualificationV2CategoryNamePipe,
  QualificationV2SelectComponent,
  QualificationsV2State,
} from '@wilson/qualification-v2/common';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { FlowDirection } from '../../../../../types';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { Store } from '@ngxs/store';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'wilson-qualifications-renderer',
  template: `
    <nz-form-item class="no-margin">
      <nz-form-control class="no-margin">
        @if (isEditable) {
          @if (
            selectedClass?.qualifications?.length > 0 &&
            flowDirection === FlowDirection.LeftToRight
          ) {
            <nz-select
              [nzDisabled]="disabled"
              nzShowSearch
              nzAllowClear
              nzMode="multiple"
              [nzPlaceHolder]="'general.search' | translate"
              [(ngModel)]="selectedQualifications"
              (ngModelChange)="onChange($event)"
              [compareWith]="compareWith"
              [nzPlacement]="'bottomRight'"
              [nzDropdownStyle]="{ 'min-width': '400px' }"
              [nzDropdownMatchSelectWidth]="false"
            >
              @for (
                qualification of qualificationsWithCategory$ | async;
                track qualification.id
              ) {
                <nz-option
                  [nzLabel]="
                    qualification.categoryName +
                    ' - ' +
                    qualification.translatedName
                  "
                  [nzValue]="qualification.id"
                >
                </nz-option>
              }
            </nz-select>
          } @else {
            <wilson-qualification-v2-select
              [placeholder]="'general.search' | translate"
              [(ngModel)]="selectedQualifications"
              [hideLabel]="true"
              [maxTagCount]="1"
              [disabled]="disabled"
              (ngModelChange)="onChange($event)"
              [customDropdownStyle]="{ 'min-width': '300px' }"
              [isDropMatchSelection]="false"
              [dropdownWidth]="'400px'"
            ></wilson-qualification-v2-select>
          }
        } @else {
          <div class="qualifications-container">
            @for (
              qualification of selectedQualificationsWithCategory$ | async;
              track qualification.id
            ) {
              <div class="qualification-row">
                <nz-tag nzColor="default" class="mx-1 qualification-tag">
                  <span
                    class="text-display"
                    nz-tooltip
                    [nzTooltipTitle]="qualification.translatedName"
                  >
                    {{ qualification.translatedName }}
                    -
                    {{ qualification.categoryName }}
                  </span>
                </nz-tag>
              </div>
            }
          </div>
        }
      </nz-form-control>
    </nz-form-item>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .no-margin {
        margin: 0 !important;
        padding: 0 !important;
      }

      nz-form-item {
        margin-bottom: 0 !important;
      }

      nz-form-control {
        margin-bottom: 0 !important;
      }

      ::ng-deep .ant-form-item {
        margin-bottom: 0 !important;
      }

      ::ng-deep .ant-form-item-control {
        margin-bottom: 0 !important;
      }

      .qualifications-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-width: 100%;
        padding: 2px 0;
      }

      .qualification-row {
        display: flex;
        align-items: center;
      }

      .text-display {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        display: block;
      }

      .qualification-tag {
        width: 150px;
        max-width: 150px;
        cursor: default;
      }

      .qualification-tag ::ng-deep .ant-tag {
        width: 100%;
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    TranslatePipe,
    QualificationV2SelectComponent,
    NzFormModule,
    NzTagModule,
    NzTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [QualificationV2CategoryNamePipe],
})
export class QualificationsRendererComponent {
  private cdr = inject(ChangeDetectorRef);
  private qualificationV2CategoryNamePipe = inject(
    QualificationV2CategoryNamePipe,
  );
  private translateService = inject(TranslateService);
  private store = inject(Store);

  @Input() set selectedClass(value: QualificationTrainingProviderClass | null) {
    if (this.flowDirection === FlowDirection.LeftToRight) {
      this.selectedQualifications = [];
    }
    this._selectedClass = value;
    this.qualificationsWithCategory$ =
      this.mapTrainingQualificationsAndCategoryName(value);
    this.updateSelectedQualificationsWithCategory();
  }
  get selectedClass(): QualificationTrainingProviderClass | null {
    return this._selectedClass;
  }

  @Input() set currentSelectedQualifications(value: string[]) {
    this._currentSelectedQualifications = value;
    this.updateSelectedQualifications();
  }
  get currentSelectedQualifications(): string[] {
    return this._currentSelectedQualifications;
  }

  @Input() index!: number;
  @Input() disabled = false;
  @Input() isEditable = false;
  @Input() flowDirection = FlowDirection.LeftToRight;
  @Output() qualificationsChange = new EventEmitter<string[]>();

  FlowDirection = FlowDirection;
  selectedQualifications: string[] = [];
  private _selectedClass: QualificationTrainingProviderClass | null = null;
  private _currentSelectedQualifications: string[] = [];
  qualificationsWithCategory$: Observable<
    (Partial<QualificationV2> & {
      categoryName: string;
      translatedName: string;
    })[]
  > = of([]);
  selectedQualificationsWithCategory$: Observable<
    (Partial<QualificationV2> & {
      categoryName: string;
      translatedName: string;
    })[]
  > = of([]);

  compareWith = (o1: QualificationId, o2: QualificationId): boolean => {
    return o1?.id === o2?.id;
  };

  private updateSelectedQualifications(): void {
    if (
      this.currentSelectedQualifications &&
      this.currentSelectedQualifications.length > 0
    ) {
      this.selectedQualifications = [...this.currentSelectedQualifications];
      this.updateSelectedQualificationsWithCategory();
    }
  }

  async onChange(qualificationsIds: string[]): Promise<void> {
    this.selectedQualifications = qualificationsIds;
    this.qualificationsChange.emit(qualificationsIds);
    this.updateSelectedQualificationsWithCategory();
    this.cdr.detectChanges();
  }

  private mapTrainingQualificationsAndCategoryName(
    selectedClass: QualificationTrainingProviderClass | null,
  ): Observable<
    (Partial<QualificationV2> & {
      categoryName: string;
      translatedName: string;
    })[]
  > {
    if (!selectedClass?.qualifications) {
      return of([]);
    }

    const qualifications = selectedClass.qualifications.map(
      (qualification) => ({
        ...qualification,
        categoryName$: this.qualificationV2CategoryNamePipe.transform(
          qualification.qualificationCategory.id,
        ),
        translatedName: this.translateService.instant(qualification.nameDe),
      }),
    );

    const categoryNames$ = qualifications.map((q) => q.categoryName$);
    return combineLatest(categoryNames$).pipe(
      map((names) =>
        qualifications.map((qualification, index) => ({
          ...qualification,
          categoryName: names[index],
        })),
      ),
    );
  }

  private updateSelectedQualificationsWithCategory(): void {
    if (this.selectedQualifications.length > 0 && !this.selectedClass) {
      this.selectedQualificationsWithCategory$ = this.store
        .select(
          QualificationsV2State.qualificationsByIds(
            this.selectedQualifications,
          ),
        )
        .pipe(
          switchMap((qualifications) => {
            if (qualifications.length === 0) {
              return of([]);
            }

            const qualificationsWithCategory = qualifications.map(
              (qualification) => ({
                ...qualification,
                categoryName$: this.qualificationV2CategoryNamePipe.transform(
                  qualification.qualificationCategoryId,
                ),
                translatedName: this.translateService.instant(
                  qualification.nameDe,
                ),
              }),
            );

            const categoryNames$ = qualificationsWithCategory.map(
              (q) => q.categoryName$,
            );
            return combineLatest(categoryNames$).pipe(
              map((names) =>
                qualificationsWithCategory.map((qualification, index) => ({
                  ...qualification,
                  categoryName: names[index],
                })),
              ),
            );
          }),
        );
    } else {
      this.selectedQualificationsWithCategory$ =
        this.qualificationsWithCategory$.pipe(
          map((qualifications) =>
            qualifications.filter(
              (qualification) =>
                qualification.id &&
                this.selectedQualifications.includes(qualification.id),
            ),
          ),
        );
    }
  }
}
